/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import FormDialog from '../../../Components/Modal/modal';
import { CANCEL_TEXT } from '../../../Constants/strings';
import dropdownArrow from '../../../Assets/images/svg/dar_dropdown_arrow.svg';

const DropdownIcon = () => (
  <img className="ai-tool-mis-dropdown-icon" src={dropdownArrow} alt="" aria-hidden="true" />
);

export const AIToolMISProjectEmployeeFields = ({
  form,
  setForm,
  employees,
  projects,
  isSaving,
  isReadOnly,
  idPrefix,
}) => {
  const [menuWidth, setMenuWidth] = useState(0);
  const selectedProjectFromOptions = projects.find(
    (project) => String(project.projectId) === String(form.projectId),
  );
  const selectedProject = selectedProjectFromOptions || (form.projectId ? {
    projectId: form.projectId,
    projectName: form.projectName,
  } : null);
  const visibleProjects = selectedProjectFromOptions || !selectedProject
    ? projects
    : [...projects, selectedProject];
  const projectEmployees = employees.filter(
    (employee) => String(employee.projectId) === String(form.projectId),
  );
  const selectedEmployeeFromOptions = projectEmployees.find(
    (employee) => String(employee.employeeId) === String(form.employeeId),
  );
  const selectedEmployee = selectedEmployeeFromOptions || (form.employeeId ? {
    projectId: form.projectId,
    employeeId: form.employeeId,
    employeeName: form.employeeName,
    employeeVertical: form.employeeVertical,
  } : null);
  const visibleEmployees = selectedEmployeeFromOptions || !selectedEmployee
    ? projectEmployees
    : [...projectEmployees, selectedEmployee];
  const selectMenuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: {
      className: 'ai-tool-mis-select-menu',
      style: {
        width: menuWidth || undefined,
        maxHeight: 'min(calc(5 * 33px + 16px), calc(100vh - 32px))',
        borderRadius: 0,
      },
    },
  };
  const handleMenuOpen = (event) => {
    setMenuWidth(event.currentTarget.getBoundingClientRect().width);
  };
  const updateProject = (projectId) => {
    const project = projects.find((option) => String(option.projectId) === projectId);
    setForm((current) => ({
      ...current,
      projectId,
      projectName: project?.projectName || '',
      projectManager: project?.seniorManager || '',
      employeeId: '',
      employeeName: '',
      employeeVertical: '',
    }));
  };
  const updateEmployee = (employeeId) => {
    const employee = projectEmployees.find(
      (option) => String(option.employeeId) === employeeId,
    );
    setForm((current) => ({
      ...current,
      employeeId,
      employeeName: employee?.employeeName || '',
      employeeVertical: employee?.employeeVertical || '',
    }));
  };

  return (
    <>
      <div className="ai-tool-mis-add-field">
        <label className="ai-tool-mis-add-label" htmlFor={`${idPrefix}-project`}>
          Project Name <span>*</span>
        </label>
        <FormControl size="small" fullWidth>
          <Select
            id={`${idPrefix}-project`}
            displayEmpty
            value={String(form.projectId || '')}
            input={<OutlinedInput className="ai-tool-mis-select-input" />}
            IconComponent={DropdownIcon}
            MenuProps={selectMenuProps}
            onOpen={handleMenuOpen}
            renderValue={(selected) => {
              if (!selected) return <span className="ai-tool-mis-placeholder">Select</span>;
              const project = visibleProjects.find(
                (option) => String(option.projectId) === selected,
              );
              return project?.projectName || selected;
            }}
            onChange={(event) => updateProject(event.target.value)}
            disabled={isSaving || isReadOnly}
          >
            <MenuItem value="">Select</MenuItem>
            {visibleProjects.map((project) => (
              <MenuItem key={project.projectId} value={String(project.projectId)}>
                {project.projectName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="ai-tool-mis-add-field">
        <label className="ai-tool-mis-add-label" htmlFor={`${idPrefix}-employee`}>
          Employee Name <span>*</span>
        </label>
        <FormControl size="small" fullWidth>
          <Select
            id={`${idPrefix}-employee`}
            displayEmpty
            value={String(form.employeeId || '')}
            input={<OutlinedInput className="ai-tool-mis-select-input" />}
            IconComponent={DropdownIcon}
            MenuProps={selectMenuProps}
            onOpen={handleMenuOpen}
            renderValue={(selected) => {
              if (!selected) return <span className="ai-tool-mis-placeholder">Select</span>;
              return selectedEmployee?.employeeName
                ? selectedEmployee.employeeName
                : selected;
            }}
            onChange={(event) => updateEmployee(event.target.value)}
            disabled={isSaving || isReadOnly || !form.projectId}
          >
            <MenuItem value="">Select</MenuItem>
            {visibleEmployees.map((employee) => (
              <MenuItem key={employee.employeeId} value={String(employee.employeeId)}>
                {employee.employeeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </>
  );
};

AIToolMISProjectEmployeeFields.propTypes = {
  form: PropTypes.shape({
    employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    employeeName: PropTypes.string,
    employeeVertical: PropTypes.string,
    projectName: PropTypes.string,
    projectManager: PropTypes.string,
  }).isRequired,
  setForm: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    employeeName: PropTypes.string,
    employeeVertical: PropTypes.string,
  })).isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    projectName: PropTypes.string,
    seniorManager: PropTypes.string,
  })).isRequired,
  isSaving: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
  idPrefix: PropTypes.string.isRequired,
};

export const AIToolMISMultiSelect = ({
  tools,
  selectedToolIds,
  onChange,
  isSaving,
  id,
}) => {
  const [menuWidth, setMenuWidth] = useState(0);
  const menuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: {
      className: 'ai-tool-mis-select-menu',
      style: {
        width: menuWidth || undefined,
        maxHeight: 'min(calc(5 * 33px + 16px), calc(100vh - 32px))',
        borderRadius: 0,
      },
    },
  };

  return (
    <div className="ai-tool-mis-add-field">
      <label className="ai-tool-mis-add-label" htmlFor={id}>
        AI Tools <span>*</span>
      </label>
      <FormControl size="small" fullWidth>
        <Select
          id={id}
          multiple
          displayEmpty
          value={selectedToolIds}
          input={<OutlinedInput className="ai-tool-mis-select-input" />}
          IconComponent={DropdownIcon}
          MenuProps={menuProps}
          onOpen={(event) => setMenuWidth(event.currentTarget.getBoundingClientRect().width)}
          renderValue={(selected) => selected.length === 0
            ? <span className="ai-tool-mis-placeholder">Select</span>
            : selected
              .map((toolId) => tools.find((tool) => Number(tool.id) === toolId)?.name)
              .filter(Boolean)
              .join(', ')}
          onChange={(event) => onChange(event.target.value.map(Number))}
          disabled={isSaving}
        >
          {tools.map((tool) => (
            <MenuItem key={tool.id} value={Number(tool.id)}>
              <Checkbox checked={selectedToolIds.includes(Number(tool.id))} />
              <ListItemText primary={tool.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

AIToolMISMultiSelect.propTypes = {
  tools: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  selectedToolIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
};

const AIToolMISAddDialog = ({
  open,
  form,
  setForm,
  employees,
  projects,
  tools,
  isLoading,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };
  const canSubmit = form.employeeId
    && form.projectId
    && form.aiToolIds.length > 0
    && form.businessJustification.trim();
  return (
    <FormDialog
      open={open}
      title="Add AI Access Details"
      handleClose={onClose}
      dialogPaperCss="ai-tool-mis-form-dialog-paper"
      dialogBodycontent={(
        <div className="ai-tool-mis-add-form">
          {isLoading ? (
            <div className="ai-tool-mis-catalog-loading"><CircularProgress size={24} /></div>
          ) : (
            <>
              <AIToolMISProjectEmployeeFields
                form={form}
                setForm={setForm}
                employees={employees}
                projects={projects}
                isSaving={isSaving}
                isReadOnly={false}
                idPrefix="ai-tool-mis-add"
              />
              <AIToolMISMultiSelect
                tools={tools}
                selectedToolIds={form.aiToolIds}
                onChange={(toolIds) => updateForm('aiToolIds', toolIds)}
                isSaving={isSaving}
                id="ai-tool-mis-add-tools"
              />
              <div className="ai-tool-mis-add-field">
                <label className="ai-tool-mis-add-label" htmlFor="ai-tool-mis-add-justification">
                  Business Justification <span>*</span>
                </label>
                <input
                  id="ai-tool-mis-add-justification"
                  className="ai-tool-mis-field-input"
                  type="text"
                  placeholder="Enter..."
                  value={form.businessJustification}
                  onChange={(event) => updateForm('businessJustification', event.target.value)}
                  disabled={isSaving}
                  required
                />
              </div>
            </>
          )}
        </div>
      )}
      button1Action={onClose}
      button2Action={onSubmit}
      button1={CANCEL_TEXT}
      button2={isSaving ? 'Adding...' : 'Add'}
      cancelButtonId="ai-tool-mis-add-cancel"
      submitButtonId="ai-tool-mis-add-submit"
      button2Disabled={isSaving || isLoading || !canSubmit}
    />
  );
};

AIToolMISAddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  form: PropTypes.shape({
    employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    aiToolIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    businessJustification: PropTypes.string.isRequired,
  }).isRequired,
  setForm: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    employeeName: PropTypes.string,
    employeeVertical: PropTypes.string,
  })).isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    projectName: PropTypes.string,
    seniorManager: PropTypes.string,
  })).isRequired,
  tools: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default AIToolMISAddDialog;
